

import {EventEmitter} from "events";
import type {DataChannel, DescriptionType, RtcConfig, PeerConnection} from "node-datachannel";
import * as nodeDataChannel from 'node-datachannel';

import { lazyLogger, WEBRTC_RELIABLE_CHANNEL, WEBRTC_UNRELIABLE_CHANNEL } from "@gotti/core";

const logger = lazyLogger('WebRTCClient', { kind: 'connector' });

type Signal = CandidateSignal | SdpSignal

export type CandidateSignal = {
    candidate: string,
    mid: string
}

export type SdpSignal = {
    sdp: string,
    type: string
}

export class WebRTCClient extends EventEmitter {
  upgradeReq?: import("http").IncomingMessage;
  auth?: any;
  seatOptions?: any;
  public peerConnection!: PeerConnection
  private localDescription!: SdpSignal;
  private collectedCandidates : Array<CandidateSignal> = [];
  private collectedSdps : Array<SdpSignal> = [];
  private collectedSignals: Array<Signal> = [];
  private bufferedCandidates : Array<CandidateSignal> = [];
  public reliableChannel!: DataChannel;
  public unreliableChannel!: DataChannel;
  public gatheringComplete: boolean = false;
  id: string;
  options: any;

  sessionId!: string;
  state!: "open" | "closed";

  private closeReason : string = '';
  private emittedClose : boolean = false;

  constructor(id: string, config: RtcConfig) {
      super();
      this.id = id;
      this.peerConnection = new nodeDataChannel.PeerConnection(id, config);
      this.peerConnection.onGatheringStateChange(state => {
        if(state === 'complete') {
            this.gatheringComplete = true;
            this.emit('complete-gathering')
        }
      })
      this.peerConnection.onLocalDescription( (sdp, type) => {
        this.addSdp({ sdp, type });
      });
      this.peerConnection.onLocalCandidate((candidate: string, mid: string) => {
          this.addCandidate({ candidate, mid });
      });
    this.reliableChannel = this.createDataChannel(WEBRTC_RELIABLE_CHANNEL, {
      negotiated: false,
    });
      this.unreliableChannel = this.createDataChannel(WEBRTC_UNRELIABLE_CHANNEL, {
          negotiated: false,
          unordered: true,
          maxRetransmits: 0,
      });
  }

  private createDataChannel(label: string, init: nodeDataChannel.DataChannelInitConfig) {
      const channel = this.peerConnection.createDataChannel(label, init);
      channel.onOpen(() => {
        this.state = 'open';
        this.emit('channel-open', label);
      });
      channel.onClosed(() => {
        this.emit('channel-closed', label);
        this.tryClose(`${label} channel closed`);
      });
      channel.onMessage((msg: string | Buffer | ArrayBuffer) => {
        this.emit('channel-message', label, msg);
      });
      channel.onError((err: string) => {
        logger.warn(`WebRTCClient channel ${label} error: ${err}`);
      });
      return channel;
  }
  tryClose(reason?: string) {
    if(this.state === "open") {
        // console.log('TRY FORCE CLOSE')
        this.close(reason);
    }
    if(!this.emittedClose) {
        this.emittedClose = true;
        this.emit('closed-channel', this.closeReason);
        this.removeAllListeners();
        // Clean up peer connection without setting null
        this.peerConnection.close();
        // nodeDataChannel.cleanup();
    }
  }
  close(reason?: string): void {
      if(reason) {
          this.closeReason = reason;
      }
      this.state = 'closed';
    
      const channels: Array<DataChannel | undefined> = [this.reliableChannel, this.unreliableChannel];
      channels.forEach((channel) => {
        if(!channel) {
          return;
        }
        try {
          channel.close();
        } catch (err) {
          const channelLabel = typeof (channel as any).getLabel === 'function' ? (channel as any).getLabel() : 'channel';
          logger.error(`WebRTCClient: Error when closing ${channelLabel}: ${err instanceof Error ? err.message : String(err)}`);
        }
      });
    //  console.log('closing peer connection...')
      try {
        this.peerConnection?.close();
      } catch (err) {
        logger.error(`WebRTCClient: Error when closing peer connection: ${err instanceof Error ? err.message : String(err)}`);
       // console.error('Caught error when closing peer connection:', err);
      }
     // Clean up peer connection without setting null
     // console.log('cleaning up node data channel...')
     // nodeDataChannel.cleanup();
  }

  public sendReliable(message: string) {
    try {
      if(!this.reliableChannel?.isOpen()) {
        throw new Error('Reliable channel not open');
      }
      this.reliableChannel.sendMessage(message);
    } catch (err) {
      logger.error(`WebRTCClient: Failed to send reliable message: ${err instanceof Error ? err.message : String(err)}`);
    }
  }

  public sendUnreliable(message: string) {
    try {
      if(!this.unreliableChannel?.isOpen()) {
        throw new Error('Unreliable channel not open');
      }
      this.unreliableChannel.sendMessage(message);
    } catch (err) {
      logger.error(`WebRTCClient: Failed to send unreliable message: ${err instanceof Error ? err.message : String(err)}`);
    }
  }

  private addCandidate(candidate: CandidateSignal) {
      this.collectedSignals.push(candidate);
      this.collectedCandidates.push(candidate)
      if(!this.localDescription) {
          this.bufferedCandidates.push(candidate)
      } else {
          this.emit('added-signal', { candidate })
      }
  }

  private addSdp(sdp: SdpSignal) {
    this.collectedSignals.push(sdp);
    this.collectedSdps.push(sdp);
    if(!this.localDescription) {
        this.initialize(sdp);
    } else {
        this.emit('added-signal', { sdp })
    }
  }

  public receivedRemoteSignal(signal: { sdp?: SdpSignal, candidate?: CandidateSignal & { sdpMid?: string} }) {
    // console.log('SERVER IS HANDLING CLIENT SIGNAL:', signal)  
    if(signal.sdp) {
          const { sdp, type } = signal.sdp;
          // console.log('SETTING REMOTE DESCRIPTION WITH SDP', { sdp, type })
          this.peerConnection.setRemoteDescription(sdp, type as DescriptionType);
      } else {
          const { candidate, mid, sdpMid } = signal.candidate;
          const _mid = mid || sdpMid;
          // console.log('SETTING REMOTE DESCRIPTION WITH CANDIDATE', { candidate, mid, sdpMid })
          this.peerConnection.addRemoteCandidate(candidate, _mid);
      }
  }

  private initialize(sdp: { sdp: string, type: string }) {
      if(this.localDescription) throw new Error(`Already initialized with a local description.`);
      this.localDescription = sdp;
      this.emit('added-signal', { sdp });
      this.bufferedCandidates.forEach(c => {
        this.emit('added-signal', { candidate: c });
      });
      this.bufferedCandidates.length = 0;
  }
}